import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useTweetVerification, VerificationType } from '@/hooks/useTweetVerification';
import { useLotteryShareImage } from '@/hooks/useLotteryShareImage';
import { getNFTMintTweetText, autoPostLotteryTweet, generateShareResult } from '@/api/pointsLottery';
import { NFTShareImageGenerator } from '@/components/NFTShareImageGenerator';
import { useShare } from '@/hooks/useShare';
import type { ShareResultResponse } from '@/types/schemas';

interface TweetVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: VerificationType;
  winnerId?: number;
  seasonId?: number;
  tweetTextOverride?: string;
  onSuccess?: () => void;
}

/**
 * Modal for tweet verification flow
 * Supports both lottery reward claims and NFT minting
 */
export function TweetVerificationModal({
  isOpen,
  onClose,
  type,
  winnerId,
  seasonId,
  tweetTextOverride,
  onSuccess,
}: TweetVerificationModalProps) {
  const verification = useTweetVerification();
  const shareImage = useLotteryShareImage();
  useShare();

  // State for NFT tweet text
  const [nftTweetText, setNftTweetText] = useState<string | null>(null);
  const [nftLoading, setNftLoading] = useState(false);
  const [nftError, setNftError] = useState<string | null>(null);

  // State for NFT share image
  const [nftShareImageUrl, setNftShareImageUrl] = useState<string | null>(null);

  // State for share result data (lottery performance)
  const [shareResultData, setShareResultData] = useState<ShareResultResponse | null>(null);

  // State for automatic posting
  const [isPosting, setIsPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [tweetUrl, setTweetUrl] = useState<string | null>(null);

  // State for manual tweet URL input (for both lottery and NFT types)
  const [manualTweetUrl, setManualTweetUrl] = useState('');

  // Generate share image when modal opens (for lottery type)
  // Or fetch NFT tweet text and share result data (for nft type)
  useEffect(() => {
    console.log('[TweetVerificationModal] Modal opened:', {
      isOpen,
      type,
      winnerId,
      seasonId,
      seasonIdType: typeof seasonId,
    });

    if (isOpen && type === 'lottery' && winnerId && seasonId) {
      console.log('[TweetVerificationModal] Generating lottery share image...');
      shareImage.generateImage(winnerId, seasonId);
    } else if (isOpen && type === 'nft' && seasonId) {
      console.log('[TweetVerificationModal] Fetching NFT tweet text and share result for season ID:', seasonId);
      // Fetch both NFT tweet text and share result data
      setNftLoading(true);
      setNftError(null);

      Promise.all([
        getNFTMintTweetText({ seasonId }),
        generateShareResult({ seasonId })
      ])
        .then(([tweetResult, shareResult]) => {
          console.log('[TweetVerificationModal] ✅ Successfully loaded NFT data:', {
            tweetText: tweetResult.tweetText?.substring(0, 50),
            shareResult,
          });
          setNftTweetText(tweetResult.tweetText);
          setShareResultData(shareResult);
          setNftLoading(false);
        })
        .catch((error) => {
          console.error('[TweetVerificationModal] ❌ Failed to load NFT data:', error);
          setNftError(error.message || 'Failed to load NFT data');
          setNftLoading(false);
        });
    } else {
      console.log('[TweetVerificationModal] Skipping data fetch:', {
        reason: !isOpen ? 'Modal not open' : !seasonId ? 'No seasonId' : 'Unknown',
      });
    }

    // Cleanup on unmount
    return () => {
      shareImage.cleanup();
      if (type === 'nft') {
        setNftTweetText(null);
        setShareResultData(null);
        setNftError(null);
        setNftShareImageUrl(null);
      }
      setIsPosting(false);
      setPostError(null);
      setTweetUrl(null);
      setManualTweetUrl('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type, winnerId, seasonId]);

  // Log when NFT share image URL is set
  useEffect(() => {
    if (nftShareImageUrl) {
      console.log('[TweetVerificationModal] ✅ NFT share image URL set:', {
        urlLength: nftShareImageUrl.length,
        urlPreview: nftShareImageUrl.substring(0, 50),
      });
    }
  }, [nftShareImageUrl]);

  if (!isOpen) return null;

  // Determine which loading state and error to use based on type
  const isLoading = type === 'lottery' ? shareImage.isLoading : nftLoading;
  const error = type === 'lottery' ? shareImage.error : nftError;

  const handleTweet = async () => {
    // For lottery type, both winnerId and seasonId are required
    // For NFT type, only seasonId is required
    if (type === 'lottery' && (!winnerId || !seasonId)) {
      setPostError('Missing required information');
      return;
    }
    if (type === 'nft' && !seasonId) {
      setPostError('Missing season information');
      return;
    }

    setIsPosting(true);
    setPostError(null);

    try {
      if (type === 'lottery') {
        if (tweetTextOverride && tweetTextOverride.trim().length > 0) {
          const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetTextOverride)}`;
          window.open(twitterUrl, '_blank', 'width=550,height=420');
          setTweetUrl('manual'); // Placeholder to show verification button
          return;
        }

        // Get Twitter intent URL for lottery
        const result = await autoPostLotteryTweet({ winnerId: winnerId!, seasonId: seasonId! });

        if (result.twitterIntentUrl) {
          // Open Twitter intent with pre-filled text
          window.open(result.twitterIntentUrl, '_blank', 'width=550,height=420');

          // Mark as "tweeted" (user needs to manually post)
          setTweetUrl('manual'); // Placeholder to show verification button
        } else {
          setPostError('Failed to generate Twitter intent URL');
        }
      } else {
        // For NFT, use Twitter intent (no auto-posting)
        if (!nftTweetText) {
          setPostError('Tweet text not loaded');
          setIsPosting(false);
          return;
        }

        // Open Twitter intent with pre-filled text
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(nftTweetText)}`;
        window.open(twitterUrl, '_blank', 'width=550,height=420');

        // Mark as "tweeted" (user needs to manually post)
        setTweetUrl('manual'); // Placeholder to show verification button
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate share URL';
      setPostError(errorMessage);
    } finally {
      setIsPosting(false);
    }
  };

  // Handle copy image to clipboard
  const handleCopyImage = async () => {
    if (!nftShareImageUrl) {
      toast.error('Image not generated yet');
      return;
    }

    try {
      // Convert data URL to properly typed blob
      const response = await fetch(nftShareImageUrl);
      const originalBlob = await response.blob();

      // Ensure blob has correct PNG MIME type (data URLs from canvas.toDataURL may lose type info)
      const pngBlob = originalBlob.type === 'image/png'
        ? originalBlob
        : new Blob([originalBlob], { type: 'image/png' });

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlob }),
      ]);
      toast.success('Image copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy image:', error);
      // Fallback: try to download instead
      toast.error('Failed to copy image. Try downloading instead.');
    }
  };

  // Handle download image
  const handleDownloadImage = () => {
    if (!nftShareImageUrl) {
      toast.error('Image not generated yet');
      return;
    }

    const link = document.createElement('a');
    link.download = `junkfun-season${shareResultData?.seasonNo || seasonId}-lottery-win.png`;
    link.href = nftShareImageUrl;
    link.click();
    toast.success('Image downloaded!');
  };

  const handleVerify = async () => {
    // Both lottery and NFT types now require manual tweet URL
    if (!manualTweetUrl.trim()) {
      toast.error('Please paste your tweet URL');
      return;
    }

    console.log('[TweetVerificationModal] handleVerify called:', {
      type,
      winnerId,
      seasonId,
      seasonIdType: typeof seasonId,
      manualTweetUrl,
    });

    const result = await verification.verifyAndComplete(type, {
      winnerId,
      seasonId,
      tweetUrl: manualTweetUrl, // Always send manual tweet URL for both types
    });

    console.log('[TweetVerificationModal] handleVerify result:', result);

    if (result.success) {
      onSuccess?.();
      setTimeout(() => {
        onClose();
        verification.reset();
      }, 1500);
    }
  };

  const handleClose = () => {
    onClose();
    verification.reset();
    shareImage.cleanup();
    setIsPosting(false);
    setManualTweetUrl('');
    setPostError(null);
    setTweetUrl(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto">
      <div className="relative w-full max-w-lg bg-gradient-to-b from-gray-900 to-gray-800 rounded-2xl p-6 border border-cyan-500/30 my-8 max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Title */}
        <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {type === 'lottery' ? 'Share Your Win!' : 'Claim Your NFT'}
        </h2>

        {/* Share image preview (for lottery) */}
        {type === 'lottery' && shareImage.imageUrl && (
          <div className="mb-6">
            <div className="rounded-lg overflow-hidden border-2 border-cyan-500/50 mb-4">
              <img
                src={shareImage.imageUrl}
                alt="Lottery win share"
                className="w-full h-auto cursor-pointer"
                onClick={async () => {
                  try {
                    const response = await fetch(shareImage.imageUrl!);
                    const originalBlob = await response.blob();
                    const pngBlob = originalBlob.type === 'image/png'
                      ? originalBlob
                      : new Blob([originalBlob], { type: 'image/png' });
                    await navigator.clipboard.write([
                      new ClipboardItem({ 'image/png': pngBlob }),
                    ]);
                    toast.success('Image copied to clipboard!');
                  } catch (error) {
                    console.error('Failed to copy image:', error);
                    toast.error('Failed to copy image. Try downloading instead.');
                  }
                }}
                title="Click to copy"
              />
            </div>
            {/* Copy and Download buttons */}
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(shareImage.imageUrl!);
                    const originalBlob = await response.blob();
                    const pngBlob = originalBlob.type === 'image/png'
                      ? originalBlob
                      : new Blob([originalBlob], { type: 'image/png' });
                    await navigator.clipboard.write([
                      new ClipboardItem({ 'image/png': pngBlob }),
                    ]);
                    toast.success('Image copied to clipboard!');
                  } catch (error) {
                    console.error('Failed to copy image:', error);
                    toast.error('Failed to copy image. Try downloading instead.');
                  }
                }}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy to Clipboard
              </button>
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.download = `junkfun-lottery-win.png`;
                  link.href = shareImage.imageUrl!;
                  link.click();
                  toast.success('Image downloaded!');
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Image
              </button>
            </div>
          </div>
        )}

        {/* NFT share image preview */}
        {type === 'nft' && nftShareImageUrl && (
          <div className="mb-6">
            <div className="rounded-lg overflow-hidden border-2 border-cyan-500/50 mb-4">
              <img
                src={nftShareImageUrl}
                alt="NFT mint share"
                className="w-full h-auto cursor-pointer"
                onClick={handleCopyImage}
                title="Click to copy"
              />
            </div>
            {/* Copy and Download buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleCopyImage}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy to Clipboard
              </button>
              <button
                onClick={handleDownloadImage}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Image
              </button>
            </div>
          </div>
        )}

        {/* Loading state */}
        {(isLoading || verification.isVerifying || isPosting) && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            <span className="ml-3 text-gray-300" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isLoading ? (type === 'lottery' ? 'Generating image...' : 'Loading tweet text...') : isPosting ? 'Opening Twitter...' : 'Verifying tweet...'}
            </span>
          </div>
        )}

        {/* Error state */}
        {(error || verification.error || postError) && (
          <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-400 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {postError || error || verification.error}
            </p>
          </div>
        )}

        {/* Tweet Posted Success */}
        {tweetUrl && !verification.isVerified && (
          <div className="mb-6 p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg">
            <div className="flex items-center mb-2">
              <svg className="w-6 h-6 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-blue-400 font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Twitter Opened!
              </p>
            </div>
            <p className="text-gray-300 text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Post your tweet with the image, then paste the tweet URL below and click "Verify Tweet".
            </p>
          </div>
        )}

        {/* Instructions */}
        {!verification.isVerified && !verification.isVerifying && !tweetUrl && (
          <div className="mb-6 space-y-4">
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h3 className="text-white font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>How it works:</h3>
              <ol className="text-gray-300 text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs mr-2">1</span>
                  <span>Copy or download your share image using the buttons above</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs mr-2">2</span>
                  <span>Click "Tweet Now" to open Twitter with pre-filled text</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs mr-2">3</span>
                  <span>Attach the image to your tweet and post it on Twitter</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs mr-2">4</span>
                  <span>Copy the tweet URL and paste it in the field below</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs mr-2">5</span>
                  <span>Click "Verify Tweet" to confirm and {type === 'lottery' ? 'claim your reward' : 'mint your NFT'}</span>
                </li>
              </ol>
            </div>

            {/* Required mentions notice */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
              <p className="text-cyan-400 text-xs" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <strong>Required:</strong> Your tweet must{' '}
                {type === 'lottery'
                  ? 'mention @junkfun_, @mantanetwork, and @bonk_inu, and include your share image'
                  : 'mention @junkfun_ and include your share image'}
                {' '}to be verified.
              </p>
            </div>
          </div>
        )}

        {/* Success state */}
        {verification.isVerified && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <p className="text-green-400 font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Tweet Verified!</p>
                <p className="text-gray-300 text-sm mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {type === 'lottery' ? 'You can now claim your reward' : 'Your NFT is ready to mint'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tweet URL Input (for both lottery and NFT manual posting) */}
        {!verification.isVerified && tweetUrl && (
          <div className="mb-4">
            <label className="block text-white text-sm font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Paste Your Tweet URL
            </label>
            <input
              type="url"
              value={manualTweetUrl}
              onChange={(e) => setManualTweetUrl(e.target.value)}
              placeholder="https://twitter.com/username/status/..."
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <p className="text-gray-400 text-xs mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              After posting your tweet with the image, paste the tweet URL here to verify
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          {!verification.isVerified ? (
            <>
              <button
                onClick={handleTweet}
                disabled={
                  isLoading ||
                  isPosting ||
                  !!tweetUrl ||
                  !seasonId ||
                  (type === 'lottery' && !winnerId)
                }
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {isPosting ? 'Posting...' : 'Tweet Now'}
              </button>
              <button
                onClick={handleVerify}
                disabled={verification.isVerifying}
                className="flex-1 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {verification.isVerifying ? 'Verifying...' : 'Verify Tweet'}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Done
            </button>
          )}
        </div>

        {/* Cancel button */}
        {!verification.isVerified && (
          <button
            onClick={handleClose}
            className="w-full mt-3 text-gray-400 hover:text-white text-sm transition-colors"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Cancel
          </button>
        )}
      </div>

      {/* NFT Share Image Generator (hidden, renders off-screen) */}
      {type === 'nft' && isOpen && shareResultData && (
        <NFTShareImageGenerator
          roi={shareResultData.roi}
          rewardSOL={shareResultData.rewardSOL}
          rewardUSD={shareResultData.rewardUSD}
          pointsInvested={shareResultData.pointsInvested}
          referralCode={shareResultData.referralCode}
          onImageGenerated={(dataUrl) => setNftShareImageUrl(dataUrl)}
        />
      )}
    </div>
  );
}
